import { createRef, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { Dropdown } from 'primereact/dropdown'

import { DOMElementRect } from '@aytm/lib/common/func'

import { calculate } from '@aytm/app/stats/lib/render/colors'
import { COLORS_TOTAL, COLOR_METHOD_OPTIONS } from '@aytm/app/stats/page/sidebar/color-options/lib/constants'

import ColorPickerPopup from '@aytm/app/stats/page/sidebar/color-options/ColorPickerPopup'

import style from '@aytm/app/stats/page/sidebar/color-options/styles/ColorLab.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

const AUTO_METHOD = '*'
const HIDE_REFRESH_FOR = ['monochromatic', 'analogous'] // FR-CLR-UI-46

// FR-CLR-UI-40..49: palette editor (the "Color Lab" section).
// - 7 color cells → click opens ColorPickerPopup anchored to the cell
// - Generation-method dropdown is always visible below the cells (FR-CLR-UI-44)
// - Refresh button at far-left of the pill, absolutely positioned; hidden for monochromatic/analogous
// - FR-CLR-UI-44: editing the anchor under a non-auto method recomputes slots 2..7
// - FR-CLR-UI-45: editing a non-anchor slot resets the method to auto and keeps the typed color
const ColorLab = ({ children, container, palette = { colors: [] }, onChange }) => {
  const [colors, setColors] = useState(palette.colors)
  const [colorsRefs, setColorsRefs] = useState([])
  const [currentColor, setCurrentColor] = useState(null)
  const [currentColorId, setCurrentColorId] = useState(null)
  const [currentMethod, setCurrentMethod] = useState(AUTO_METHOD)

  const pickerRef = useRef(null)
  const popupRef = useRef(null)
  const pickerTopRef = useRef(0)

  const popupOpen = null !== currentColor && null !== currentColorId
  const refreshHidden = HIDE_REFRESH_FOR.includes(currentMethod)

  const closePopup = useCallback(() => {
    setCurrentColor(null)
    setCurrentColorId(null)
  }, [])

  const onRefresh = () => {
    onChange(
      calculate(
        AUTO_METHOD === currentMethod ? null : colors[0],
        AUTO_METHOD === currentMethod ? 'random' : currentMethod,
        COLORS_TOTAL
      )
    )
  }

  const onColorChange = value => {
    setCurrentColor(value)
    const normalized = value.toUpperCase()

    if (0 === currentColorId && AUTO_METHOD !== currentMethod) {
      onChange(calculate(normalized, currentMethod, COLORS_TOTAL))
      return
    }

    if (0 !== currentColorId) setCurrentMethod(AUTO_METHOD)
    const next = colors.slice()
    next[currentColorId] = normalized
    onChange(next)
  }

  const onMethodChange = event => {
    const next = event.value
    if (next === currentMethod) return
    setCurrentMethod(next)
    if (AUTO_METHOD !== next) onChange(calculate(colors[0], next, COLORS_TOTAL))
  }

  const onCellSelect = index => {
    setCurrentColor(colors[index])
    setCurrentColorId(index)
  }

  // Close popup if the picker row scrolled off-screen (its offsetParent goes null).
  const clickHandler = useCallback(() => {
    if (!container) return
    if (pickerRef.current && null === pickerRef.current.offsetParent) closePopup()
  }, [container, closePopup])

  // Keep popup glued to the picker row while the sidebar scrolls.
  const scrollHandler = useCallback(() => {
    if (!container || !popupRef.current || !pickerRef.current) return

    const pickerRect = DOMElementRect(pickerRef.current)
    if (pickerRect.top === pickerTopRef.current) return

    const containerRect = DOMElementRect(container)
    const popupRect = DOMElementRect(popupRef.current)
    popupRef.current.style.top = `${Math.min(
      containerRect.height - popupRect.height,
      Math.max(0, popupRect.top + (pickerRect.top - pickerTopRef.current))
    )}px`
    pickerTopRef.current = pickerRect.top
  }, [container])

  useEffect(() => {
    setColors(palette.colors)
  }, [palette])

  useEffect(() => {
    setColorsRefs(existing =>
      Array(colors.length)
        .fill()
        .map((_, i) => existing[i] || createRef())
    )
    if (null !== currentColorId) setCurrentColor(colors[currentColorId])
  }, [colors, currentColorId])

  useEffect(() => {
    if (!popupOpen) return

    if (null !== pickerRef.current) {
      pickerTopRef.current = DOMElementRect(pickerRef.current).top
    }

    document.addEventListener('click', clickHandler)
    document.addEventListener('scroll', scrollHandler)

    return () => {
      document.removeEventListener('click', clickHandler)
      document.removeEventListener('scroll', scrollHandler)
    }
  }, [popupOpen, clickHandler, scrollHandler])

  return (
    <>
      <div ref={pickerRef} className={style['palette-picker-item']}>
        {refreshHidden ? null : (
          <span
            data-testid="stat-color-options-lab-refresh"
            className={style['palette-picker-random']}
            aria-label="Random colors"
            {...pressable(onRefresh)}
          >
            <i className="icon-aytmfd-refresh-v2" />
          </span>
        )}
        <div className={style['palette-picker-colors']}>
          {colors.map((color, index) => (
            <span
              ref={colorsRefs[index]}
              key={`palette-picker-color-${index}`}
              data-testid={`stat-color-options-lab-cell-${index}`}
              className={`${style['palette-picker-colors-item']}${
                index === currentColorId ? ' ' + style['palette-picker-colors-item-current'] : ''
              }`}
              aria-label={`Color ${index + 1}`}
              {...pressable(() => onCellSelect(index))}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        {children}
      </div>

      <div className={style['palette-picker-methods']}>
        <div className="p-fluid">
          <Dropdown
            data-testid="stat-color-options-lab-dropdown"
            className="p-dropdown-sm p-dropdown-rounded p-dropdown-no-scroll"
            aria-label="Color method"
            ariaLabel="Color method"
            value={currentMethod}
            options={COLOR_METHOD_OPTIONS}
            optionValue="value"
            optionLabel="text"
            fixedDropdownDir="bottom"
            onChange={onMethodChange}
          />
        </div>
      </div>

      {popupOpen && (
        <ColorPickerPopup
          ref={popupRef}
          container={container}
          target={colorsRefs[currentColorId]?.current}
          color={currentColor}
          offsetTop={29}
          offsetLeft={286 - 24 * currentColorId}
          onChange={onColorChange}
          onClose={closePopup}
        />
      )}
    </>
  )
}

ColorLab.propTypes = {
  children: PropTypes.any,
  container: PropTypes.any,
  palette: PropTypes.object,
  onChange: PropTypes.func
}

ColorLab.displayName = 'ColorLab'

export default ColorLab
